import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import {
	Box,
	Typography,
	IconButton,
	FormControlLabel,
	Switch,
	Modal,
	Alert,
} from "@mui/material";
import Avatar from "@mui/material/Avatar";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import { MutatingDots } from "react-loader-spinner";
import SearchIcon from "@mui/icons-material/Search";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import RoomIcon from "@mui/icons-material/Room";
import CoffeeIcon from "@mui/icons-material/Coffee";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import ContactSupportIcon from "@mui/icons-material/ContactSupport";
import HandshakeIcon from "@mui/icons-material/Handshake";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CloseIcon from "@mui/icons-material/Close";
import { useIntl } from "react-intl";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import XIcon from "@mui/icons-material/X";
import InstagramIcon from "@mui/icons-material/Instagram";
import FacebookIcon from "@mui/icons-material/Facebook";
import YouTubeIcon from "@mui/icons-material/YouTube";
import LinkIcon from "@mui/icons-material/Link";
import "swiper/swiper-bundle.css";
import { FormattedMessage } from "react-intl";
import { cleanEmptyHTMLTags } from "./hooks/cleanEmptyHTMLTags";
import { useDispatch, useSelector } from "react-redux";
import {
	emptySpeaker,
	getPrograms,
	getSpeakersDetail,
} from "./store/slices/program";
import PoweredBy from "./component/PoweredBy";
import WidgetTitle from "./component/WidgetTitle";
import SearchBox from "./component/SearchBox";

const toMinutes = (time) => {
	const [hour, minute] = (time ?? "").split(":").map(Number);
	return (hour || 0) * 60 + (minute || 0);
};

// Oturumun sponsoru varsa logosunu gösterir; link doluysa yeni sekmede açar.
const SessionSponsor = ({ sponsor, className = "" }) => {
	if (!sponsor?.isSupport || !sponsor?.logo) return null;

	const logo = <img src={sponsor.logo} alt="Sponsor" loading="lazy" />;

	return (
		<Box className={`session-sponsor ${className}`}>
			<span className="session-sponsor__label">
				<FormattedMessage id="sponsor" />
			</span>
			{sponsor.link ? (
				<a
					href={sponsor.link}
					target="_blank"
					rel="noopener noreferrer"
					className="session-sponsor__logo"
					// Karta tıklanmış sayılıp oturum modalı açılmasın
					onClick={(e) => e.stopPropagation()}
					onKeyDown={(e) => e.stopPropagation()}
				>
					{logo}
				</a>
			) : (
				<span className="session-sponsor__logo">{logo}</span>
			)}
		</Box>
	);
};

const ProgramWidget = ({
	language,
	eventID,
	organiserID,
	color,
	displayTitle,
	headerTitle,
	topics,
	programDayTopic,
	defaultShowAll, // Add defaultShowAll to props
}) => {
	const intl = useIntl();
	const dispatch = useDispatch();
	const [searchQuery, setSearchQuery] = useState("");
	const [activeDay, setActiveDay] = useState(0);
	const [showAll, setShowAll] = useState(false); // Initial state set to false
	const [selectedSalons, setSelectedSalons] = useState({});
	const [selectedSession, setSelectedSession] = useState({});
	const swiperRef = useRef(null);

	const [modalOpen, setModalOpen] = useState(false);
	const [sessionModalOpen, setSessionModalOpen] = useState(false);

	const { error, loading, program } = useSelector((state) => state.program);
	const speakerDetails = useSelector((state) => state.program.speaker);

	const sortedProgram = useMemo(() => {
		if (!Array.isArray(program)) return [];
		return [...program].sort((a, b) => {
			const [dayA, monthA, yearA] = a.date.split("/");
			const dateA = new Date(`${yearA}-${monthA}-${dayA}`);
			const [dayB, monthB, yearB] = b.date.split("/");
			const dateB = new Date(`${yearB}-${monthB}-${dayB}`);
			return dateA - dateB;
		});
	}, [program]);

	useEffect(() => {
		dispatch(
			getPrograms({
				organiserID: organiserID,
				eventID: eventID,
				programDayTopic: programDayTopic,
				language: language,
			})
		);
	}, [eventID, organiserID, language, programDayTopic, dispatch]);

	const handleDayChange = useCallback(
		(newValue) => {
			if (!Array.isArray(sortedProgram) || sortedProgram.length === 0) {
				return;
			}

			const dayData = sortedProgram[newValue];
			if (!dayData) return;

			setActiveDay(newValue);

			if (defaultShowAll) {
				const allSelected = {};
				dayData.halls.forEach((salon) => {
					allSelected[salon.id] = true;
				});
				setSelectedSalons(allSelected);
				setShowAll(true);
			} else {
				// Sadece ilk salonu seçili yap
				const firstSalonSelected = {};
				if (dayData.halls.length > 0) {
					firstSalonSelected[dayData.halls[0].id] = true;
				}
				setSelectedSalons(firstSalonSelected);
				setShowAll(false); // "Tümünü Göster" kapalı olsun
			}
		},
		[sortedProgram, defaultShowAll]
	);

	useEffect(() => {
		if (!Array.isArray(sortedProgram) || sortedProgram.length === 0) {
			return;
		}

		const today = new Date();
		const day = String(today.getDate()).padStart(2, "0");
		const month = String(today.getMonth() + 1).padStart(2, "0");
		const year = today.getFullYear();
		const todayFormatted = `${day}/${month}/${year}`;

		const todayIndex = sortedProgram.findIndex(
			(p) => p.date === todayFormatted
		);
		const initialDayIndex = todayIndex !== -1 ? todayIndex : 0;

		handleDayChange(initialDayIndex);
		if (swiperRef.current) {
			swiperRef.current.slideTo(initialDayIndex, 0);
		}
	}, [sortedProgram, handleDayChange]);

	if (loading || error) {
		return (
			<Box className="widget-spinner">
				<MutatingDots
					visible={true}
					height="100"
					width="100"
					color="#4fa94d"
					secondaryColor="#4fa94d"
					radius="12.5"
					ariaLabel="mutating-dots-loading"
					wrapperStyle={{}}
					wrapperClass=""
				/>
			</Box>
		);
	}

	const handleSalonToggle = (salonId) => {
		const dayData = sortedProgram[activeDay];
		setSelectedSalons((prev) => {
			const updatedSalons = { ...prev, [salonId]: !prev[salonId] };
			const allSelected = dayData.halls.every(
				(salon) => updatedSalons[salon.id]
			);
			setShowAll(allSelected);
			return updatedSalons;
		});
	};

	const handleShowAllToggle = () => {
		const dayData = sortedProgram[activeDay];
		const newShowAll = !showAll;

		setShowAll(newShowAll);

		if (newShowAll) {
			const allSelected = {};
			dayData.halls.forEach((salon) => {
				allSelected[salon.id] = true;
			});
			setSelectedSalons(allSelected);
		} else {
			setSelectedSalons({});
		}
	};

	const filteredOturumlar = () => {
		if (!Array.isArray(sortedProgram) || sortedProgram.length === 0) {
			return [];
		}

		const dayData = sortedProgram[activeDay] || null;
		if (!dayData) return [];

		// Zaman çizelgesi görünümü için birden fazla salon seçiliyken de
		// oturumları başlangıç saatine göre sırala.
		return dayData.halls
			.filter((salon) => showAll || selectedSalons[salon.id])
			.flatMap((salon) => salon.programs)
			.sort((a, b) => toMinutes(a.startTime) - toMinutes(b.startTime));
	};

	const dayColor = sortedProgram[activeDay]?.color || color;
	const hallSwitchSx = {
		"& .MuiSwitch-switchBase.Mui-checked": {
			color: color,
		},
		"& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
			backgroundColor: "#000",
		},
	};
	const dayHalls = sortedProgram[activeDay]?.halls ?? [];
	const hallNameBySession = {};
	dayHalls.forEach((salon) =>
		salon.programs.forEach((p) => {
			hallNameBySession[p.id] = salon.name;
		})
	);

	const parseDayDate = (date) => {
		const [day, month, year] = (date ?? "").split("/").map(Number);
		return new Date(year, month - 1, day);
	};

	// Gün adı zaten haftanın günü ise ("Çarşamba") tarihte tekrar yazma
	const formatDayDate = (date, dayName) => {
		const parsed = parseDayDate(date);
		if (isNaN(parsed)) return date;
		const locale = language === "en" ? "en-GB" : "tr-TR";
		const weekday = parsed.toLocaleDateString(locale, { weekday: "long" });
		const sameAsName =
			weekday.toLocaleLowerCase(locale) ===
			(dayName ?? "").trim().toLocaleLowerCase(locale);
		// "4 Kasım, 2026" biçimi
		const month = parsed.toLocaleDateString(locale, { month: "long" });
		const formatted = `${parsed.getDate()} ${month}, ${parsed.getFullYear()}`;
		return sameAsName ? formatted : `${formatted} · ${weekday}`;
	};

	const social = speakerDetails?.socialMedia ?? {};
	const speakerSocialLinks = [
		{ href: social.linkend, label: "LinkedIn", Icon: LinkedInIcon },
		{ href: social.twitter, label: "X", Icon: XIcon },
		{ href: social.instagram, label: "Instagram", Icon: InstagramIcon },
		{ href: social.facebook, label: "Facebook", Icon: FacebookIcon },
		{ href: social.youtube, label: "YouTube", Icon: YouTubeIcon },
		{ href: social.website, label: "Website", Icon: LinkIcon },
	].filter((item) => item.href);

	const selectedHallCount = dayHalls.filter(
		(salon) => showAll || selectedSalons[salon.id]
	).length;

	const isSessionLive = (session) => {
		// TEMP disabled
		return false;
		const dayDate = parseDayDate(sortedProgram[activeDay]?.date);
		const now = new Date();
		if (isNaN(dayDate) || dayDate.toDateString() !== now.toDateString()) {
			return false;
		}
		const nowMinutes = now.getHours() * 60 + now.getMinutes();
		return (
			nowMinutes >= toMinutes(session?.startTime) &&
			nowMinutes < toMinutes(session?.endTime)
		);
	};

	const searchedOturumlar = filteredOturumlar().filter((oturum) => {
		const lowerCaseQuery = searchQuery.toLowerCase();

		// Oturum adı içinde arama
		const isMatchInSessionName = oturum.title
			.toLowerCase()
			.includes(lowerCaseQuery);

		// Konuşmacıların isimleri içinde arama
		const isMatchInSpeakers = oturum.speakers?.some((speaker) =>
			speaker.name.toLowerCase().includes(lowerCaseQuery)
		);

		// Eğer oturum adı ya da konuşmacı adı eşleşiyorsa, bu oturumu döndür
		return isMatchInSessionName || isMatchInSpeakers;
	});
	const handlePreviousClick = () => {
		swiperRef.current?.slidePrev();
	};

	const handleNextClick = () => {
		swiperRef.current?.slideNext();
	};

	// Modal
	const handleSpeakerClick = (speaker) => {
		setSessionModalOpen(false);
		setModalOpen(true);

		dispatch(
			getSpeakersDetail({
				id: speaker.id,
				organiserID: organiserID,
				eventID: eventID,
				language: language,
			})
		);
	};

	const handleCloseModal = () => {
		setModalOpen(false);
		dispatch(emptySpeaker());
	};

	const handleSessionClick = (session) => {
		if (!Array.isArray(sortedProgram) || sortedProgram.length === 0) {
			return;
		}

		const matchedProgram =
			sortedProgram.find((item) => item.id === session.programDayId) || null;
		const matchedHall =
			matchedProgram?.halls?.find(
				(hall) => hall.id === session.programHallId
			) || null;
		setSelectedSession({
			...session,
			matchedProgram,
			matchedHall,
		});
		setSessionModalOpen(true);
	};

	const handleSessionCloseModal = () => {
		setSessionModalOpen(false);
		setSelectedSession(null);
	};
	const BreakIcon = ({ type }) => {
		let IconComponent;

		switch (type) {
			case "dinner":
				IconComponent = RestaurantIcon;
				break;
			case "coffe-cup":
				IconComponent = CoffeeIcon;
				break;
			case "question":
				IconComponent = ContactSupportIcon;
				break;
			case "handshake":
				IconComponent = HandshakeIcon;
				break;
			case "gift":
				IconComponent = CardGiftcardIcon;
				break;
			default:
				IconComponent = CoffeeIcon; // varsayılan ikon
		}

		return (
			<IconComponent
				style={{
					color: "#8b8b8b",
					fontSize: "32px",
				}}
			/>
		);
	};

	if (loading) {
		return (
			<Box className="widget-spinner">
				<MutatingDots
					visible={true}
					height="100"
					width="100"
					color="#4fa94d"
					secondaryColor="#4fa94d"
					radius="12.5"
					ariaLabel="mutating-dots-loading"
				/>
			</Box>
		);
	}
		console.log("program", program);
	return (
		<>
			{!Array.isArray(sortedProgram) || sortedProgram.length === 0 ? (
				<Box className="widget-block--search-not">
					<Alert severity="info">
						<FormattedMessage id="programNotFound" />
					</Alert>
				</Box>
			) : (
				<div className="widget-block" style={{ "--color": color }}>
					{displayTitle && (
						<WidgetTitle title={headerTitle}><FormattedMessage id="program" /></WidgetTitle>
					)}
					<Box
						className="program-agenda"
						style={{ "--day-color": dayColor }}
					>
						{sortedProgram.length > 1 && (
							<Box className="program-agenda__days">
								<IconButton
									className="program-agenda__days-nav program-agenda__days-nav--prev"
									onClick={handlePreviousClick}
									aria-label="previous"
								>
									<KeyboardArrowLeftIcon />
								</IconButton>
								<Swiper
									spaceBetween={10}
									slidesPerView={Math.min(4, sortedProgram.length)}
									onSlideChange={(swiper) =>
										handleDayChange(swiper.activeIndex)
									}
									initialSlide={activeDay}
									modules={[Navigation]}
									onSwiper={(swiper) => (swiperRef.current = swiper)}
									navigation={false}
									breakpoints={{
										320: {
											slidesPerView: Math.min(2, sortedProgram.length),
											spaceBetween: 8,
										},
										768: {
											slidesPerView: Math.min(3, sortedProgram.length),
											spaceBetween: 10,
										},
										1024: {
											// Gün sayısı 4'ten azsa kutular tüm genişliği doldursun
											slidesPerView: Math.min(4, sortedProgram.length),
											spaceBetween: 12,
										},
									}}
								>
									{sortedProgram.map((day, index) => (
										<SwiperSlide key={day.id}>
											<Box
												component="button"
												type="button"
												role="tab"
												aria-selected={index === activeDay}
												className={`program-agenda__day${
													index === activeDay ? " is-active" : ""
												}`}
												onClick={() => handleDayChange(index)}
											>
												<span className="program-agenda__day-name">
													{day.name}
												</span>
												<span className="program-agenda__day-date">
													{formatDayDate(day.date, day.name)}
												</span>
											</Box>
										</SwiperSlide>
									))}
								</Swiper>
								<IconButton
									className="program-agenda__days-nav program-agenda__days-nav--next"
									onClick={handleNextClick}
									aria-label="next"
								>
									<ChevronRightIcon />
								</IconButton>
							</Box>
						)}

						<Box className="program-agenda__toolbar">
							<Box className="program-agenda__halls">
								<FormControlLabel
									className="program-agenda__hall"
									control={
										<Switch
											checked={showAll}
											onChange={handleShowAllToggle}
											sx={hallSwitchSx}
										/>
									}
									label={
										<Typography
											variant="span"
											className="program-agenda__hall-label"
										>
											<FormattedMessage id="showAll" />
										</Typography>
									}
								/>
								{dayHalls.map((salon) => (
									<FormControlLabel
										key={salon.id}
										className="program-agenda__hall"
										control={
											<Switch
												checked={!!selectedSalons[salon.id]}
												onChange={() => handleSalonToggle(salon.id)}
												sx={hallSwitchSx}
											/>
										}
										label={
											<Typography
												variant="span"
												className="program-agenda__hall-label"
											>
												{salon.name}
											</Typography>
										}
									/>
								))}
							</Box>

							<SearchBox
								value={searchQuery}
								onChange={setSearchQuery}
								placeholderId="searchSpeakersSession"
								count={
									<FormattedMessage
										id="sessionCount"
										values={{
											count: searchedOturumlar.filter(
												(o) => o?.type !== "section"
											).length,
										}}
									/>
								}
							/>
						</Box>

						{searchedOturumlar.length === 0 ? (
							<Box className="program-agenda__empty">
								<SearchIcon />
								<FormattedMessage id="sessionNotFound" />
							</Box>
						) : (
							<Box className="program-agenda__list">
								{searchedOturumlar.map((oturum, index) => {
									if (oturum?.type === "section") {
										return (
											<Box className="program-agenda__section" key={index}>
												{oturum?.title}
											</Box>
										);
									}

									const isBreak = oturum?.type === "break";
									const isLive = isSessionLive(oturum);
									const hallName =
										selectedHallCount > 1 ? hallNameBySession[oturum.id] : null;

									return (
										<Box className="program-agenda__row" key={index}>
											<Box className="program-agenda__time">
												<span className="program-agenda__time-start">
													{oturum?.startTime}
												</span>
												<span className="program-agenda__time-end">
													{oturum?.endTime}
												</span>
											</Box>
											<Box className="program-agenda__rail" aria-hidden="true">
												<span
													className={`program-agenda__dot${
														isLive ? " is-live" : ""
													}`}
												/>
											</Box>
											<Box
												className={`program-agenda__card${
													isBreak ? " is-break" : ""
												}${isLive ? " is-live" : ""}`}
												// Molalar modalda yeni bilgi göstermediği için tıklanmaz
												{...(!isBreak && {
													role: "button",
													tabIndex: 0,
													onClick: () => handleSessionClick(oturum),
													onKeyDown: (e) => {
														if (e.key === "Enter" || e.key === " ") {
															e.preventDefault();
															handleSessionClick(oturum);
														}
													},
												})}
											>
												<Box className="program-agenda__card-main">
													<Box className="program-agenda__meta">
														<span className="program-agenda__meta-time">
															<AccessTimeIcon />
															{oturum?.startTime} – {oturum?.endTime}
														</span>
														{hallName && (
															<span className="program-agenda__meta-hall">
																<RoomIcon />
																{hallName}
															</span>
														)}
														{isLive && (
															<span className="program-agenda__live">
																<FormattedMessage id="liveNow" />
															</span>
														)}
													</Box>
													<Box className="program-agenda__title">
														{isBreak && (
															<span className="program-agenda__break-icon">
																<BreakIcon type={oturum?.iconType} />
															</span>
														)}
														{oturum?.title}
													</Box>
													{oturum?.speakers?.length > 0 && (
														<Box className="program-agenda__speakers">
															{oturum.speakers.map((item, sIndex) => (
																<Box
																	component="button"
																	type="button"
																	key={sIndex}
																	className="program-agenda__speaker"
																	onClick={(e) => {
																		e.stopPropagation();
																		handleSpeakerClick(item);
																	}}
																	onKeyDown={(e) => e.stopPropagation()}
																>
																	<Avatar
																		alt={item?.name}
																		src={item?.speakerImagePath}
																		className="program-agenda__speaker-avatar"
																	/>
																	<span className="program-agenda__speaker-text">
																		<span className="program-agenda__speaker-name">
																			{item?.name}
																		</span>
																		{item?.title && (
																			<span className="program-agenda__speaker-title">
																				{item?.title}
																			</span>
																		)}
																	</span>
																</Box>
															))}
														</Box>
													)}
												</Box>
												<SessionSponsor
													sponsor={oturum?.sponsor}
													className="program-agenda__sponsor"
												/>
												{!isBreak && (
													<ChevronRightIcon className="program-agenda__chevron" />
												)}
											</Box>
										</Box>
									);
								})}
							</Box>
						)}
					</Box>
					{modalOpen && (
						<Modal
							open={modalOpen}
							aria-labelledby="program-speaker-modal-title"
							onClose={handleCloseModal}
						>
							<Box
								className="program-modal"
								style={{ "--day-color": dayColor }}
							>
								<Box className="program-modal__bar">
									<Typography
										variant="span"
										className="program-modal__bar-title"
									>
										<FormattedMessage id="modalSpeakerTitle" />
									</Typography>
									<IconButton
										onClick={handleCloseModal}
										aria-label={intl.formatMessage({ id: "close" })}
										size="small"
									>
										<CloseIcon />
									</IconButton>
								</Box>

								{!speakerDetails?.name ? (
									<Box className="program-modal__loading">
										<MutatingDots
											visible={true}
											height="80"
											width="80"
											color={dayColor}
											secondaryColor={dayColor}
											radius="10"
											ariaLabel="mutating-dots-loading"
										/>
									</Box>
								) : (
									<Box className="program-modal__body">
										<Box className="program-modal__profile">
											<SessionSponsor
												sponsor={speakerDetails?.sponsor}
												className="program-modal__sponsor"
											/>
											<Avatar
												alt={speakerDetails?.name}
												src={speakerDetails?.speakerImagePath}
												className="program-modal__avatar"
											/>
											<Typography
												id="program-speaker-modal-title"
												variant="span"
												className="program-modal__name"
											>
												{speakerDetails?.name}
											</Typography>
											{speakerDetails?.title && (
												<Typography
													variant="span"
													className="program-modal__role"
												>
													{speakerDetails?.title}
												</Typography>
											)}
											{cleanEmptyHTMLTags(speakerDetails?.companyName) && (
												<Box
													className="program-modal__company"
													dangerouslySetInnerHTML={{
														__html: speakerDetails?.companyName,
													}}
												/>
											)}
											{speakerSocialLinks.length > 0 && (
												<Box className="program-modal__socials">
													{speakerSocialLinks.map(({ href, label, Icon }) => (
														<IconButton
															key={label}
															component="a"
															href={href}
															target="_blank"
															rel="noopener noreferrer"
															aria-label={label}
															className="program-modal__social"
														>
															<Icon />
														</IconButton>
													))}
												</Box>
											)}
										</Box>

										{cleanEmptyHTMLTags(speakerDetails?.biography) && (
											<Box className="program-modal__section">
												<Typography
													variant="span"
													className="program-modal__section-title"
												>
													<FormattedMessage id="biography" />
												</Typography>
												<Box
													className="program-modal__richtext"
													dangerouslySetInnerHTML={{
														__html: cleanEmptyHTMLTags(
															speakerDetails?.biography
														),
													}}
												/>
											</Box>
										)}

										{speakerDetails?.programs?.length > 0 && (
											<Box className="program-modal__section">
												<Typography
													variant="span"
													className="program-modal__section-title"
												>
													<FormattedMessage id="speakerSessions" />
												</Typography>
												<Box className="program-modal__sessions">
													{speakerDetails.programs.map((item, key) => (
														<Box className="program-modal__session" key={key}>
															<Box className="program-modal__session-time">
																<span>{item?.startTime}</span>
																<small>{item?.endTime}</small>
															</Box>
															<Box className="program-modal__session-info">
																<span className="program-modal__session-title">
																	{item?.title}
																</span>
																<span className="program-modal__session-meta">
																	<CalendarMonthIcon />
																	{formatDayDate(item?.day?.date)}
																	{item?.hall?.name && (
																		<>
																			<RoomIcon />
																			{item.hall.name}
																		</>
																	)}
																</span>
															</Box>
														</Box>
													))}
												</Box>
											</Box>
										)}
									</Box>
								)}
							</Box>
						</Modal>
					)}

					{sessionModalOpen && selectedSession && (
						<Modal
							open={sessionModalOpen}
							aria-labelledby="program-session-modal-title"
							onClose={handleSessionCloseModal}
						>
							<Box
								className="program-modal program-modal--session"
								style={{
									"--day-color":
										selectedSession?.matchedProgram?.color || dayColor,
								}}
							>
								<Box className="program-modal__head">
									<Box className="program-modal__head-text">
										{isSessionLive(selectedSession) && (
											<span className="program-agenda__live">
												<FormattedMessage id="liveNow" />
											</span>
										)}
										<Typography
											id="program-session-modal-title"
											variant="span"
											className="program-modal__title"
										>
											{selectedSession?.title}
										</Typography>
									</Box>
									<IconButton
										onClick={handleSessionCloseModal}
										aria-label={intl.formatMessage({ id: "close" })}
										size="small"
										className="program-modal__close"
									>
										<CloseIcon />
									</IconButton>
								</Box>

								<Box className="program-modal__body">
									<Box className="program-modal__facts">
										{selectedSession?.matchedProgram?.date && (
											<Box className="program-modal__fact">
												<CalendarMonthIcon />
												<span className="program-modal__fact-label">
													<FormattedMessage id="sessionDate" />
												</span>
												<span className="program-modal__fact-value">
													{formatDayDate(selectedSession.matchedProgram.date)}
												</span>
											</Box>
										)}
										<Box className="program-modal__fact">
											<AccessTimeIcon />
											<span className="program-modal__fact-label">
												<FormattedMessage id="sessionTime" />
											</span>
											<span className="program-modal__fact-value">
												{selectedSession?.startTime} – {selectedSession?.endTime}
											</span>
										</Box>
										{selectedSession?.matchedHall?.name && (
											<Box className="program-modal__fact">
												<RoomIcon />
												<span className="program-modal__fact-label">
													<FormattedMessage id="sessionHall" />
												</span>
												<span className="program-modal__fact-value">
													{selectedSession.matchedHall.name}
												</span>
											</Box>
										)}
									</Box>

									{cleanEmptyHTMLTags(selectedSession?.content) && (
										<Box className="program-modal__section">
											<Typography
												variant="span"
												className="program-modal__section-title"
											>
												<FormattedMessage id="sessionAbout" />
											</Typography>
											<Box
												className="program-modal__richtext"
												dangerouslySetInnerHTML={{
													__html: cleanEmptyHTMLTags(selectedSession?.content),
												}}
											/>
										</Box>
									)}

									{selectedSession?.speakers?.length > 0 && (
										<Box className="program-modal__section">
											<Typography
												variant="span"
												className="program-modal__section-title"
											>
												<FormattedMessage id="speaker" />
											</Typography>
											<Box className="program-modal__speakers">
												{selectedSession.speakers.map((row, index) => (
													<Box
														component="button"
														type="button"
														key={index}
														className="program-modal__speaker"
														onClick={() => handleSpeakerClick(row)}
													>
														<Avatar
															alt={row?.name}
															src={row?.speakerImagePath}
															className="program-modal__speaker-avatar"
														/>
														<span className="program-modal__speaker-text">
															<span className="program-modal__speaker-name">
																{row?.name}
															</span>
															{row?.title && (
																<span className="program-modal__speaker-title">
																	{row?.title}
																</span>
															)}
														</span>
														<ChevronRightIcon className="program-modal__speaker-chevron" />
													</Box>
												))}
											</Box>
										</Box>
									)}

									{selectedSession?.sponsor?.isSupport &&
										selectedSession?.sponsor?.logo && (
											<Box className="program-modal__section">
												<Typography
													variant="span"
													className="program-modal__section-title"
												>
													<FormattedMessage id="sponsor" />
												</Typography>
												<SessionSponsor
													sponsor={selectedSession.sponsor}
													className="program-modal__sponsor-row"
												/>
											</Box>
										)}
								</Box>
							</Box>
						</Modal>
					)}
					<PoweredBy />
				</div>
			)}
		</>
	);
};

export default ProgramWidget;
